import { AnimatePresence, motion } from 'motion/react';
import { api, type Orden } from '../api';
import { aparecer, fila } from '../components/movimiento';
import { BarraFila } from '../components/graficos';
import { Buscador, Cargando, ChipBoton, DetalleOrden, Numero, EstadoChip, filtrar, Paginador, PanelError, usePaginado, Vacio } from '../components/ui';
import { useEstadoGuardado } from '../lib/persistencia';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useState } from 'react';
import { useEsCelular } from '../lib/useMedia';
import { aviso, botonSecundario, c, encabezadoTabla, fechaImpresa, filaTabla, mono, tarjeta, HUSO } from '../theme';

/**
 * Todas las órdenes de trabajo en una lista. Reemplaza a la vieja entrada de
 * Fraccionamiento, que mezclaba las órdenes abiertas con los pallets en mesa:
 * el trabajo va de a orden, y cada una se completa desde su ficha.
 */
const FILTROS = [
  { id: 'abiertas', texto: 'Abiertas', es: (o: Orden) => o.estado === 'Pendiente' || o.estado === 'En armado' },
  { id: 'despachadas', texto: 'Despachadas', es: (o: Orden) => o.estado === 'Despachada' },
  { id: 'canceladas', texto: 'Canceladas', es: (o: Orden) => o.estado === 'Cancelada' },
  { id: 'todas', texto: 'Todas', es: () => true },
] as const;

const fechaHora = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', { timeZone: HUSO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function Ordenes({ onAbrir }: { onAbrir: (id: string) => void }) {
  // Lo que ZF pide entra acá sin que nadie recargue.
  const { datos, cargando, error, recargar } = useApi(() => api.ordenes(), [], VIVO.rapido);
  // Se vuelve a esta lista todo el tiempo: filtro y búsqueda sobreviven al F5.
  const [filtro, setFiltro] = useEstadoGuardado<string>('ordenes.filtro', 'abiertas');
  const [texto, setTexto] = useEstadoGuardado('ordenes.busqueda', '');

  const todas = datos ?? [];
  const activo = FILTROS.find((f) => f.id === filtro) ?? FILTROS[0];
  const encontradas = filtrar(todas.filter(activo.es), texto, (o) => [
    o.id,
    o.detalle,
    o.estado,
    o.pedidaPor,
    o.observaciones,
    ...o.articulos,
  ]);
  const pagina = usePaginado(encontradas);
  const celular = useEsCelular();
  // En el celular el renglón se acomoda en tres líneas: el código con el estado
  // arriba, qué pide en el medio, y cuándo y cuánto avanzó abajo. `orden` los
  // reordena sin tocar el markup de la PC.
  const enCelular = (orden: number, ancho100?: boolean): React.CSSProperties =>
    celular ? { order: orden, width: ancho100 ? '100%' : 'auto', minWidth: 0 } : {};

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Buscando las órdenes…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ArmadosLibres onCambio={recargar} />
      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center">
        <div className="wms-tira">
        {FILTROS.map((f) => (
          <ChipBoton key={f.id} grupo="ordenes-filtro" activo={f.id === activo.id} onClick={() => setFiltro(f.id)}>
            {f.texto} · <Numero valor={todas.filter(f.es).length} />
          </ChipBoton>
        ))}
        </div>
        <span className="hidden flex-1 tablet:block" />
        {/* Sin el envoltorio, el flex:1 del Buscador en la columna del celular le comía el alto (20 px). */}
        <div className="flex w-full tablet:w-auto tablet:flex-1 tablet:max-w-[300px]">
          <Buscador valor={texto} onCambiar={setTexto} placeholder="OT, artículo o quién la pidió" ancho={celular ? 9999 : 300} />
        </div>
      </div>

      <motion.div {...aparecer} style={{ ...tarjeta, overflow: 'hidden' }}>
        {encontradas.length === 0 ? (
          <Vacio
            icono="assignment"
            titulo={todas.length === 0 ? 'Todavía no hay órdenes' : 'Sin órdenes'}
            detalle={
              todas.length === 0
                ? 'Las órdenes que pida ZF aparecen acá.'
                : texto
                  ? 'Ninguna orden coincide con lo que buscaste.'
                  : `No hay órdenes ${activo.texto.toLowerCase()}.`
            }
          />
        ) : (
          <div className={celular ? undefined : 'wms-scroll-x'}>
            <div>
              <div style={{ ...encabezadoTabla, display: celular ? 'none' : 'flex' }}>
                <span style={{ width: 96 }}>Orden</span>
                <span style={{ flex: 1, minWidth: 240 }}>Qué pide</span>
                <span style={{ width: 170 }}>Pedida</span>
                <span style={{ width: 180 }}>Avance</span>
                <span style={{ width: 110 }}>Estado</span>
              </div>
              {/* Al filtrar o buscar las que quedan se deslizan a su lugar; al cambiar de página entran otras. */}
              <div style={{ position: 'relative' }}>
              <AnimatePresence mode="popLayout" initial={false}>
              {pagina.visibles.map((o, i) => (
                <motion.button
                  key={o.id}
                  layout="position"
                  {...fila(i)}
                  type="button"
                  onClick={() => onAbrir(o.id)}
                  title={`Abrir la ${o.id}`}
                  style={{ ...filaTabla, width: '100%', border: 0, borderBottom: filaTabla.borderBottom, background: 'none', font: 'inherit', textAlign: 'left', cursor: 'pointer', ...(celular ? { flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' } : {}) }}
                >
                  <span style={{ width: 96, fontFamily: mono, fontSize: 13, fontWeight: 600, color: c.verde, ...enCelular(1) }}>{o.id}</span>
                  <span style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 2, ...(celular ? { ...enCelular(3, true), flex: '1 1 100%' } : {}) }}>
                    <DetalleOrden orden={o} />
                    {o.observaciones && <span style={{ fontSize: 12, color: c.texto3, textWrap: 'pretty' }}>{o.observaciones}</span>}
                    {o.faltante && <span style={{ fontSize: 12, color: c.ambar, textWrap: 'pretty' }}>Se cerró con faltante: {o.faltante}.</span>}
                  </span>
                  <span style={{ width: 170, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12.5, color: c.texto2, ...enCelular(4, true) }}>
                    <span>{fechaHora(o.creadaEn)}</span>
                    {o.pedidaPor && (
                      <span style={{ color: c.texto3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.pedidaPor}>
                        {o.pedidaPor}
                      </span>
                    )}
                    {o.entregaEstimada && <span style={{ color: c.texto3 }}>entrega {fechaImpresa(o.entregaEstimada)}</span>}
                  </span>
                  {/* Cuánto ya salió: es lo que se mira para saber cómo viene. */}
                  <span style={{ width: 180, display: 'flex', alignItems: 'center', gap: 10, ...enCelular(5, true) }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <BarraFila
                        valor={o.entregadas}
                        maximo={Math.max(o.salidas, 1)}
                        color={c.verde}
                        descripcion={`${o.id}: ${o.entregadas} de ${o.salidas} entregados`}
                      />
                    </span>
                    <span style={{ fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {o.entregadas}/{o.salidas}
                    </span>
                  </span>
                  <span style={{ width: 110, ...(celular ? { order: 2, marginLeft: 'auto', width: 'auto' } : {}) }}>
                    <EstadoChip estado={o.estado} />
                  </span>
                </motion.button>
              ))}
              </AnimatePresence>
              </div>
            </div>
          </div>
        )}
        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="órdenes"
          onIr={pagina.setPagina}
        />
      </motion.div>
    </div>
  );
}

/**
 * Los capachos que quedaron armados sin orden —sacados de una o guardados al
 * cerrarla— con la orden en que se pueden poner: tocarla acepta ese pedido con
 * el capacho armado. Sin ninguno libre no ocupa lugar.
 */
function ArmadosLibres({ onCambio }: { onCambio: () => void }) {
  const { datos, recargar } = useApi(() => api.armadosLibres(), [], VIVO.rapido);
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<{ tono: 'ok' | 'err'; texto: string } | null>(null);
  if (!datos || datos.length === 0) return null;

  async function poner(armado: string, capacho: string, orden: string) {
    setOcupado(true);
    try {
      await api.usarArmado(capacho, armado);
      setMensaje({ tono: 'ok', texto: `${armado} pasó a la ${orden} y quedó entregado.` });
    } catch (err) {
      setMensaje({ tono: 'err', texto: mensajeDeError(err) });
    } finally {
      setOcupado(false);
      recargar();
      onCambio();
    }
  }

  return (
    <motion.div {...aparecer} style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>
        Capachos armados sin orden · <Numero valor={datos.length} />
      </span>
      {datos.map((a) => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: c.texto2 }}>
          <span style={{ fontFamily: mono, fontWeight: 600, color: c.texto }}>{a.id}</span>
          <span style={{ flex: 1, minWidth: 200 }}>
            <span style={{ fontFamily: mono }}>{a.articulo}</span> · {a.piezas} pz
            {a.pallet && ` · de ${a.pallet} · ${a.ubicacion ?? 'sin ubicar'}`}
          </span>
          {a.destinos.length === 0 ? (
            <span style={{ fontSize: 12, color: c.texto3 }}>Ninguna orden pide {a.articulo} en un capacho donde entre.</span>
          ) : (
            a.destinos.slice(0, 3).map((d) => (
              <button
                key={d.orden}
                type="button"
                disabled={ocupado}
                onClick={() => void poner(a.id, d.capacho, d.orden)}
                title={`Usarlo en ${d.capacho} (${d.piezas} pz)`}
                style={{ ...botonSecundario, padding: '6px 12px', fontSize: 12.5, borderColor: c.verdeBorde, color: c.verde, opacity: ocupado ? 0.6 : 1 }}
              >
                Poner en {d.orden}
              </button>
            ))
          )}
        </div>
      ))}
      {mensaje && <div style={aviso(mensaje.tono)}>{mensaje.texto}</div>}
    </motion.div>
  );
}
